import { useFormik } from "formik";
import * as Yup from "yup";
import { emailRegx, urlRegx } from "@/services/schemas/genericSchemas";
import { useSelector } from "react-redux";
import { selectCountries } from "@/store/slices/general/countriesSlice";
import { cleanedPhoneNumber } from "@/services/api-data-mapping";
import { getPhoneValue } from "@/services/generic.methods";
import useFeatures from "@/hooks/useFeatures";
import { FeatureKeys } from "@/services/dropdown-enums";

export const FormHandler = ({ sections, handleSubmit, handleValidate }) => {
  const countries = useSelector(selectCountries);
  const { hasFeature } = useFeatures();
  const isCRM = hasFeature(FeatureKeys.CRM);
  const getValidationRegex = ({ validation, pattern, alternateTo }) => {
    let reg = null;
    switch (validation) {
      case "email":
        reg = new RegExp(emailRegx);
        break;

      case "url":
        reg = new RegExp(urlRegx);
        break;

      case "custom":
        reg = new RegExp(pattern);
        break;

      default:
        reg = new RegExp("^.*$", "g");
        break;
    }

    return reg;
  };

  const applyRules = (f, formValues) => {
    const {
      name,
      rules,
      title,
      multiSelect = false,
      multiple = false,
      parentType,
      subType,
    } = f;
    let field = multiSelect || multiple ? Yup.array() : Yup.string();
    if (rules) {
      if (rules.required && name !== "email") field = field.required(`${title} is required`);

      if (rules.limit)
        field = field.max(rules.limit, `${title} exceded from 50 charaters.`);

      if (rules.validation) {
        if (rules.validation === "minMaxNumber") {
          field = validateNumberRange(rules, title);
        } else if (isPhone(rules, name)) {
          field = validatePhone(formValues, f, field);
        } else if (name === "email") {
          field = field.test(
            "email-or-phone",
            isCRM ? "Email or Phone is required" : "Email is required",
            function (value) {
              const phoneVal = formValues?.phone;
              return (value && value.trim() !== "") || (phoneVal && phoneVal.trim() !== "");
            }
          ).test(
            "valid-email-format",
            rules?.invalidError ?? "Invalid Email",
            function (value) {
              if (!value || value.trim() === "") return true;
              return new RegExp(emailRegx).test(value);
            }
          );
        } else {
          if (rules.alternateTo) {
            field = field.test(
              "is-duplicate-email",
              rules?.alternateError ?? "Duplicate error", // Error message if validation fails
              () => formValues?.[name] !== formValues?.[rules.alternateTo],
            );
          } else {
            field = field.matches(
              getValidationRegex(rules),
              rules?.invalidError ?? `Invalid ${title}`,
            );
          }
        }
      }
    }

    if (parentType?.toLowerCase() === "datetime" && formValues?.[name]) {
      let type = "input";

      switch (subType) {
        case "dateandtime":
          type = "date and time";
          break;
        case "date":
          type = "date";
          break;
        case "time":
          type = "time";
          break;
      }

      field = Yup.mixed().test(
        "is-valid",
        `${title} must be a valid ${type}`,
        (value) => value !== "Invalid date",
      );
    }

    return field;
  };

  const makeSchema = (values) => {
    let schema = {};
    let initial = {};
    sections.forEach((section) => {
      if (section.fields && section.fields.length)
        section.fields.forEach((f) => {
          schema[f.name] = applyRules(f, values);
          initial[f.name] = f.value;
        });
    });
    return { schema: Yup.object().shape(schema), initial: initial };
  };

  const validateNumberRange = (rules, title) => {
    let min = rules.min,
      max = rules.max,
      minErr = `at least ${min} digit${min > 1 ? "s" : ""}`,
      maxErr = `at most ${max} digit${max > 1 ? "s" : ""}`;
    if (rules.isBetween) {
      min = min + 1;
      max = max - 1;
      minErr = `greater than ${rules.min} digit${rules.min > 1 ? "s" : ""}`;
      maxErr = `less than ${rules.max} digit${rules.max > 1 ? "s" : ""}`;
    }
     
    const baseSchema = Yup.number().transform((value, originalValue) => {
      if (
        originalValue === "" ||
        originalValue == null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      if (typeof originalValue === "number") {
        return Number.isFinite(originalValue) ? originalValue : undefined;
      }
      const parsed = parseFloat(String(originalValue).trim());
      return Number.isFinite(parsed) ? parsed : undefined;
    });

    // Decimal fields (e.g. currency) accept any finite number — negative and
    // fractional values below 1 are all valid, so digit-count range tests are skipped.
    if (rules.allowDecimal) {
      return baseSchema;
    }

    const minValidation = min == 0 ? 0 : Math.pow(10, min - 1);
    const maxValidation = Math.pow(10, max) - 1;

    return (
      baseSchema
        // Negative values are supported: we validate the absolute magnitude
        // so that "-123" is treated as having 3 digits when min=3, max=3.
        .test(
          "min-digits",
          () => `Number must be ${minErr}`,
          (value) => value == null || Math.abs(value) >= minValidation,
        )
        .test(
          "max-digits",
          () => `Number must be ${maxErr}`,
          (value) => value == null || Math.abs(value) <= maxValidation,
        )
    );
  };

  const validatePhone = (formValues, field, yup, minLength = 8) => {
    // Retrieve the value of the primary field
    let phone = cleanedPhoneNumber(formValues?.[field.name] || "");
    const selectedCountry = (countries?.countries || []).find(
      (country) => country.code == formValues?.countryISOCode,
    );
    const rippedCountryCode = selectedCountry?.dial_code;

    if (field?.rules?.alternateTo) {
      const primaryFieldValue = formValues?.[field?.rules?.alternateTo];
      if (
        primaryFieldValue &&
        rippedCountryCode + phone ===
          formValues?.countryCode + primaryFieldValue
      ) {
        return yup.test(
          "is-duplicate-phone",
          field?.rules?.alternateError ?? "Duplicate error", // Error message if validation fails
          () => false, // Validation function, returns false in any case
        );
      }
    }
    if (formValues?.[field.name]) {
      const mask = selectedCountry ? selectedCountry?.phone_mask : null;
      if (mask) {
        phone = getPhoneValue(phone, selectedCountry);
        // Convert mask to regex pattern if it exists
        const regexPattern = cleanedPhoneNumber(mask || "")
          .replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&") // Escape special characters
          .replace(/9/g, "\\d"); // Replace '9' with '\d' to match any digit

        const regex = new RegExp(`^${regexPattern}$`);
        // Create and return a RegExp from the pattern
        return yup.test(
          "is-valid-phone",
          field?.rules?.invalidError ?? `Invalid ${field?.title}`, // Error message if validation fails
          () => {
            // Allow empty phone if not required
            if (!phone) return true;
            return regex.test(phone);
          }, // Validation function, returns true if the value matches the regex
        );
      } else {
        // If no mask is available, check if the number meets the minimum length requirement
        return yup.min(
          minLength,
          field?.rules?.invalidError ?? `Invalid ${field?.title}`,
        );
      }
    }
  };
  const isPhone = (rules, name) => {
    return (
      rules.validation === "phone" ||
      name === "countryCode" ||
      name === "alternateCountryCode"
    );
  };

  return useFormik({
    initialValues: sections.reduce((acc, section) => {
      (section.fields || []).forEach((field) => {
        acc[field.name] = field.value || "";
      });
      return acc;
    }, {}),
    validationSchema: Yup.lazy((values) => makeSchema(values).schema),
    onSubmit: handleSubmit,
    validate: handleValidate,
  });
};
